"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"

import { Evaluate, zeroAddress } from "viem"
import type { GetVaultErrorType } from "../actions/getVault"
import {
  type GetVaultData,
  type GetVaultOptions,
  type GetVaultQueryFnData,
  type GetVaultQueryKey,
  getVaultQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseVaultParameters<
  config extends Config = Config,
  selectData = GetVaultData,
> = Evaluate<
  GetVaultOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetVaultQueryFnData,
                GetVaultErrorType,
                selectData,
                GetVaultQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetVaultQueryFnData) => selectData
    }
>

export type UseVaultReturnType<selectData = GetVaultData> = UseQueryReturnType<
  selectData,
  GetVaultErrorType
>

export function useVault<
  config extends ExtendedConfig,
  selectData = GetVaultData,
>(
  parameters: UseVaultParameters<config, selectData> = {},
): UseVaultReturnType<selectData> {
  const {
    query = {},
    address,
    evcAddress,
    vaultLensAddress,
    scopeKey,
  } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getVaultQueryOptions(config, {
    address,
    chainId: parameters.chainId ?? chainId,
    evcAddress,
    vaultLensAddress,
    scopeKey,
  })
  const enabled = Boolean(
    address && address !== zeroAddress && (query.enabled ?? true),
  )

  return useQuery({
    ...query,
    ...options,
    queryKey,
    retry: false,
    refetchOnMount: false,
    enabled,
  })
}
